type MultiSelectInstance = {
  wrapper: HTMLElement;
  control: HTMLButtonElement;
  menu: HTMLElement;
  setMenuOpen: (open: boolean) => void;
};

let idCounter = 0;
const instances: MultiSelectInstance[] = [];
let delegatedListenersAttached = false;

function attachDelegatedListenersOnce() {
  if (delegatedListenersAttached) return;
  delegatedListenersAttached = true;

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;

    instances.forEach(({ wrapper, setMenuOpen }) => {
      if (!wrapper.contains(target)) {
        setMenuOpen(false);
      }
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;

    instances.forEach(({ menu, control, setMenuOpen }) => {
      if (!menu.hidden) {
        setMenuOpen(false);
        control.focus();
      }
    });
  });
}

function enhanceOptimizelyMultiSelects(root: Document = document) {
  attachDelegatedListenersOnce();

  root
    .querySelectorAll<HTMLSelectElement>(".Form__Element.FormSelection select[multiple]")
    .forEach((select) => {
      if (select.dataset.multiSelectEnhanced === "true") return;

      const formElement = select.closest<HTMLElement>(".FormSelection");
      const caption = formElement?.querySelector<HTMLLabelElement>(
        ".Form__Element__Caption"
      );
      const placeholderOption = Array.from(select.options).find(
        (option) => option.disabled
      );
      const placeholderText = placeholderOption?.text.trim() || "Välj alternativ";

      if (!select.id) {
        select.id = `form-selection-${idCounter++}`;
      }

      select.dataset.multiSelectEnhanced = "true";
      select.tabIndex = -1;
      select.setAttribute("aria-hidden", "true");
      formElement?.classList.add("FormSelection--enhanced");

      const controlId = `${select.id}__control`;
      const menuId = `${select.id}__menu`;

      if (caption) {
        caption.htmlFor = controlId;
        caption.id = caption.id || `${select.id}__label`;
      }

      const wrapper = document.createElement("div");
      wrapper.className = "FormSelection__custom";

      const control = document.createElement("button");
      control.type = "button";
      control.id = controlId;
      control.className = "FormSelection__control";
      control.setAttribute("aria-expanded", "false");
      control.setAttribute("aria-controls", menuId);

      const describedBy = select.getAttribute("aria-describedby");
      if (describedBy) {
        control.setAttribute("aria-describedby", describedBy);
      }

      const values = document.createElement("span");
      values.className = "FormSelection__values";
      values.id = `${select.id}__values`;

      const chevron = document.createElement("span");
      chevron.className = "FormSelection__chevron";
      chevron.setAttribute("aria-hidden", "true");

      control.append(values, chevron);

      const menu = document.createElement("div");
      menu.id = menuId;
      menu.className = "FormSelection__menu";
      menu.setAttribute("role", "group");

      if (caption) {
        menu.setAttribute("aria-labelledby", caption.id);
        control.setAttribute("aria-labelledby", `${caption.id} ${values.id}`);

      }
      menu.hidden = true;

      wrapper.append(control, menu);
      select.insertAdjacentElement("afterend", wrapper);

      const selectableOptions = Array.from(select.options).filter(
        (option) => !option.disabled && option.value !== ""
      );

      // Kryssrutorna skapas 
      const checkboxByValue = new Map<string, HTMLInputElement>();

      selectableOptions.forEach((option) => {
        const label = document.createElement("label");
        label.className = "FormSelection__option";

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.checked = option.selected;
        checkbox.value = option.value;

        const optionText = document.createElement("span");
        optionText.textContent = option.text.trim();

        checkbox.addEventListener("change", () => {
          option.selected = checkbox.checked;

          // Krävs för Optimizely Forms-validering och villkorsstyrda fält.
          select.dispatchEvent(new Event("input", { bubbles: true }));
          select.dispatchEvent(new Event("change", { bubbles: true }));
        });

        label.append(checkbox, optionText);
        menu.append(label);
        checkboxByValue.set(option.value, checkbox);
      });

      // Synkar bara kryssrutornas "checked"-status — bygger inte om DOM:en.
      function syncCheckboxes() {
        selectableOptions.forEach((option) => {
          const checkbox = checkboxByValue.get(option.value);
          if (checkbox && checkbox.checked !== option.selected) {
            checkbox.checked = option.selected;
          }
        });
      }

      function renderValues() {
        const selected = selectableOptions.filter((option) => option.selected);

        values.replaceChildren();

        if (!selected.length) {
          const placeholder = document.createElement("span");
          placeholder.className = "FormSelection__placeholder";
          placeholder.textContent = placeholderText;
          values.append(placeholder);
          return;
        }

        selected.forEach((option) => {
          const value = document.createElement("span");
          value.className = "FormSelection__value";

          const valueText = document.createElement("span");
          valueText.className = "FormSelection__value-text";
          valueText.textContent = option.text.trim();

          const removeButton = document.createElement("button");
          removeButton.type = "button";
          removeButton.className = "FormSelection__value-remove";
          removeButton.setAttribute("aria-label", `Ta bort valt alternativ: ${option.text.trim()}`);
          removeButton.innerHTML =
            '<svg height="14" width="14" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path></svg>';

          removeButton.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            option.selected = false;

            select.dispatchEvent(new Event("input", { bubbles: true }));
            select.dispatchEvent(new Event("change", { bubbles: true }));
          });

          value.append(valueText, removeButton);
          values.append(value);
        });
      }

      function render() {
        renderValues();
        syncCheckboxes();
      }

      function setMenuOpen(open: boolean) {
        control.setAttribute("aria-expanded", String(open));
        menu.hidden = !open;
        formElement?.classList.toggle("FormSelection--open", open);
      }

      control.addEventListener("click", () => {
        setMenuOpen(control.getAttribute("aria-expanded") !== "true");
      });

      control.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

        event.preventDefault();

        const isOpen = control.getAttribute("aria-expanded") === "true";
        if (!isOpen) {
          setMenuOpen(true);
        }

        const checkboxes = Array.from(
          menu.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
        );

        if (event.key === "ArrowDown") {
          checkboxes[0]?.focus();
        } else {
          checkboxes[checkboxes.length - 1]?.focus();
        }
      });

       menu.addEventListener("keydown", (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.type !== "checkbox") return;

        const checkboxes = Array.from(
          menu.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
        );
        const currentIndex = checkboxes.indexOf(target);

        switch (event.key) {
          case "Enter": {
            event.preventDefault();
            target.checked = !target.checked;
            target.dispatchEvent(new Event("change", { bubbles: true }));
            break;
          }
          case "ArrowDown": {
            event.preventDefault();
            checkboxes[currentIndex + 1]?.focus();
            break;
          }
          case "ArrowUp": {
            event.preventDefault();
            checkboxes[currentIndex - 1]?.focus();
            break;
          }
        }
      });

      // select:ens native change-event triggas både av klick på kryssrutor och
      // programmatiska ändringar (t.ex. reset, extern kod); synkar utan att bygga om menyn.
      select.addEventListener("change", render);

      select.form?.addEventListener("reset", () => {
        window.setTimeout(render, 0);
      });

      instances.push({ wrapper, control, menu, setMenuOpen });

      render();
    });
}

document.addEventListener("DOMContentLoaded", () => {
  enhanceOptimizelyMultiSelects();
});